import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Apple, Facebook, Instagram, Lock, Menu, ShieldCheck } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { useLocalePath } from "@/components/LangRoutes";
import { useOrgIdentity } from "@/hooks/useSubscriptionPayments";


const useT = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "ar").split("-")[0];
  const t = (ar: string, en: string, fr: string) => (lang === "en" ? en : lang === "fr" ? fr : ar);
  return { t, lang };
};

const navItems: { to: string; label: (t: (a: string, e: string, f: string) => string) => string }[] = [
  { to: "/", label: (t) => t("الرئيسية", "Home", "Accueil") },
  { to: "/features", label: (t) => t("المزايا", "Features", "Fonctionnalités") },
  { to: "/pricing", label: (t) => t("الأسعار", "Pricing", "Tarifs") },
  { to: "/blog", label: (t) => t("المدونة", "Blog", "Blog") },
  { to: "/help", label: (t) => t("مركز المساعدة", "Help Center", "Centre d'aide") },
  { to: "/contact", label: (t) => t("تواصل معنا", "Contact", "Contact") },
];

export const MarketingLayout = () => {
  const { user } = useAuth();
  const { t, lang } = useT();
  const lp = useLocalePath();
  const { i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const side = i18n.dir() === "rtl" ? "left" : "right";
  const { data: org } = useOrgIdentity();
  const orgName = (lang === "ar" ? org?.legal_name_ar : org?.legal_name_en) || org?.legal_name_ar || org?.legal_name_en || null;
  const orgAddress = [
    org?.addr_building && org?.addr_street ? `${org.addr_street} ${org.addr_building}` : org?.addr_street,
    org?.addr_district,
    org?.addr_city,
    org?.addr_postal_code,
    org?.addr_country,
  ]
    .filter(Boolean)
    .join(", ");
  const hasOrg = !!(orgName || org?.cr_number || orgAddress || org?.contact_phone);
  const socialLinks = [
    {
      href: "https://www.facebook.com/sida.business.ksa",
      label: "Facebook",
      icon: Facebook,
    },
    {
      href: "https://www.instagram.com/sida.business.ksa/",
      label: "Instagram",
      icon: Instagram,
    },
  ];



  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 gap-2">
          <Link to={lp("/")} className="flex items-center shrink-0" aria-label="SIDA">
            <img src="/sida-marketing-logo.webp" alt="SIDA" width={400} height={107} loading="eager" fetchPriority="high" decoding="async" className="h-10 sm:h-12 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={lp(n.to)}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`
                }
              >
                {n.label(t)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <ThemeToggle className="hidden sm:inline-flex" />
            <div className="hidden sm:block">
              <LanguageSwitcher asLinks />
            </div>
            {user ? (
              <Button asChild size="sm" className="hidden sm:inline-flex">
                <Link to="/dashboard">{t("افتح التطبيق", "Open App", "Ouvrir")}</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex">
                  <Link to="/auth">{t("دخول", "Sign in", "Connexion")}</Link>
                </Button>
                <Button asChild size="sm" className="hidden sm:inline-flex">
                  <Link to="/auth?mode=signup">{t("ابدأ مجاناً", "Start Free", "Démarrer")}</Link>
                </Button>
              </>
            )}

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side={side} className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="px-5 h-14 flex items-center border-b border-border">
                    <img src="/sida-marketing-logo.webp" alt="SIDA" width={400} height={107} loading="lazy" decoding="async" className="h-10 w-auto object-contain" />
                  </div>
                  <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {navItems.map((n) => (
                      <NavLink
                        key={n.to}
                        to={lp(n.to)}
                        end={n.to === "/"}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                            isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                          }`
                        }
                      >
                        {n.label(t)}
                      </NavLink>
                    ))}
                  </nav>
                  <div className="p-3 border-t border-border space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <LanguageSwitcher asLinks />
                      <ThemeToggle variant="outline" />
                    </div>
                    {user ? (
                      <Button asChild className="w-full" onClick={() => setMobileOpen(false)}>
                        <Link to="/dashboard">{t("افتح التطبيق", "Open App", "Ouvrir")}</Link>
                      </Button>
                    ) : (
                      <>
                        <Button asChild variant="outline" className="w-full" onClick={() => setMobileOpen(false)}>
                          <Link to="/auth">{t("دخول", "Sign in", "Connexion")}</Link>
                        </Button>
                        <Button asChild className="w-full" onClick={() => setMobileOpen(false)}>
                          <Link to="/auth?mode=signup">{t("ابدأ مجاناً", "Start Free", "Démarrer")}</Link>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1"><RouteErrorBoundary><Outlet /></RouteErrorBoundary></main>

      <footer className="border-t border-border/60 bg-muted/30 mt-12 sm:mt-16">
        <div className="container mx-auto px-4 py-8 sm:py-12 grid gap-8 sm:grid-cols-2 md:grid-cols-4">
          <div className="sm:col-span-2 md:col-span-2">
            <div className="flex items-center mb-3">
              <img src="/sida-marketing-logo.webp" alt="SIDA" width={400} height={107} loading="lazy" decoding="async" className="h-12 w-auto object-contain" />
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              {t(
                "برنامج محاسبة سحابي متكامل للشركات السعودية. سهل، آمن، ومتوافق مع متطلبات هيئة الزكاة والضريبة.",
                "Cloud accounting software for Saudi businesses. Simple, secure, and ZATCA-compliant.",
                "Logiciel comptable cloud pour entreprises saoudiennes. Simple, sécurisé, conforme ZATCA.",
              )}
            </p>
            {hasOrg && (
              <address className="mt-4 not-italic text-xs text-muted-foreground space-y-1">
                {orgName && <div className="font-semibold text-foreground/90">{orgName}</div>}
                {org?.cr_number && (
                  <div>
                    {t("السجل التجاري", "Commercial registration", "Registre de commerce")}:{" "}
                    <span className="font-mono">{org.cr_number}</span>
                  </div>
                )}
                {org?.vat_number && (
                  <div>
                    {t("الرقم الضريبي", "VAT number", "N° TVA")}: <span className="font-mono">{org.vat_number}</span>
                  </div>
                )}
                {orgAddress && <div>{orgAddress}</div>}
                {org?.addr_short && (
                  <div>
                    {t("العنوان المختصر", "Short address", "Adresse courte")}:{" "}
                    <span className="font-mono">{org.addr_short}</span>
                  </div>
                )}
                {org?.contact_phone && (
                  <div>
                    {t("الجوال", "Phone", "Téléphone")}:{" "}
                    <a href={`tel:${org.contact_phone}`} className="font-mono hover:text-foreground" dir="ltr">
                      {org.contact_phone}
                    </a>
                  </div>
                )}
                {org?.contact_email && (
                  <div>
                    <a href={`mailto:${org.contact_email}`} className="hover:text-foreground" dir="ltr">
                      {org.contact_email}
                    </a>
                  </div>
                )}
              </address>
            )}
          </div>

          <div>
            <h4 className="font-semibold mb-3 text-sm">{t("المنتج", "Product", "Produit")}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to={lp("/features")} className="hover:text-foreground">{t("المزايا","Features","Fonctionnalités")}</Link></li>
              <li><Link to={lp("/pricing")} className="hover:text-foreground">{t("الأسعار","Pricing","Tarifs")}</Link></li>
              <li><Link to={lp("/compare")} className="hover:text-foreground">{t("المقارنة مع البدائل","Compare alternatives","Comparer les alternatives")}</Link></li>
              <li><Link to={lp("/blog")} className="hover:text-foreground">{t("المدونة","Blog","Blog")}</Link></li>
              <li><Link to={lp("/help")} className="hover:text-foreground">{t("مركز المساعدة","Help Center","Centre d'aide")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3 text-sm">{t("قانوني", "Legal", "Légal")}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to={lp("/legal/terms")} className="hover:text-foreground">{t("الشروط","Terms","Conditions")}</Link></li>
              <li><Link to={lp("/legal/privacy")} className="hover:text-foreground">{t("الخصوصية","Privacy","Confidentialité")}</Link></li>
              <li><Link to={lp("/legal/refund")} className="hover:text-foreground">{t("الاسترجاع","Refund","Remboursement")}</Link></li>
              <li><Link to={lp("/security")} className="hover:text-foreground">{t("الأمان","Security","Sécurité")}</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => import("@/lib/cookieConsent").then(m => m.openCookieSettings())}
                  className="hover:text-foreground text-start"
                >
                  {t("إعدادات الكوكيز","Cookie settings","Paramètres des cookies")}
                </button>
              </li>
              <li><Link to={lp("/contact")} className="hover:text-foreground">{t("تواصل","Contact","Contact")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-y border-border/60 bg-background/60">
          <div className="container mx-auto flex flex-col gap-6 px-4 py-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <span className="shrink-0 text-sm font-medium text-muted-foreground">
                {t("تابعنا على", "Follow us", "Suivez-nous")}
              </span>
              <div className="flex items-center gap-2">
                {socialLinks.map(({ href, label, icon: Icon }) => (
                  <Button
                    key={label}
                    asChild
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 rounded-full bg-card shadow-sm transition-transform hover:-translate-y-0.5 hover:border-primary/40 hover:text-primary"
                  >
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${t("تابع SIDA على", "Follow SIDA on", "Suivez SIDA sur")} ${label}`}
                      title={label}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </a>
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <span className="shrink-0 text-sm font-medium text-muted-foreground">
                {t("طرق الدفع الآمنة", "Secure payment methods", "Moyens de paiement sécurisés")}
              </span>
              <div
                className="flex max-w-full flex-wrap items-center justify-center gap-1.5 rounded-lg border border-border bg-card p-1.5 shadow-sm"
                aria-label={t("طرق الدفع المتاحة", "Available payment methods", "Moyens de paiement disponibles")}
              >
                <span className="flex h-8 min-w-14 items-center justify-center rounded-md bg-muted px-2 text-sm font-extrabold text-primary" aria-label="Visa">
                  VISA
                </span>
                <span className="flex h-8 min-w-14 items-center justify-center gap-0.5 rounded-md bg-muted px-2" aria-label="Mastercard">
                  <span className="h-4 w-4 rounded-full bg-destructive/80" aria-hidden="true" />
                  <span className="-ms-1.5 h-4 w-4 rounded-full bg-warning/80" aria-hidden="true" />
                </span>
                <span className="flex h-8 min-w-14 items-center justify-center rounded-md bg-muted px-2 text-sm font-bold text-foreground" aria-label="mada">
                  mada
                </span>
                <span className="flex h-8 min-w-20 items-center justify-center gap-1 rounded-md bg-muted px-2 text-xs font-semibold text-foreground" aria-label="Apple Pay">
                  <Apple className="h-4 w-4" aria-hidden="true" />
                  Pay
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="py-4 px-4">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>{t("بياناتك مشفّرة ومعزولة.","Your data is encrypted and isolated.","Vos données sont chiffrées et isolées.")}</span>
            </div>
            <Link to={lp("/security")} className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              {t("اعرف المزيد عن الأمان","Learn more about security","En savoir plus sur la sécurité")}
            </Link>
          </div>
        </div>
        <div className="border-t border-border/60 py-4 text-center text-xs text-muted-foreground px-4">
          © {new Date().getFullYear()} SIDA. {t("جميع الحقوق محفوظة.","All rights reserved.","Tous droits réservés.")}
        </div>
      </footer>
    </div>
  );
};
